import React, { useState, useEffect } from 'react';
import { Box, Typography, Pagination } from '@mui/material';
import { useSelector } from 'react-redux';
import axios from 'axios';
// 👉 Heart icon
import heartIcon from '../../../assets/wish4.png';
// 👉 Import loader
import Loader from '../../../Loader/Loader';
// 👉 Import selector from Redux store
import { selectUser } from '../../../Commonlibs/userSlice';
// 👉 Import useNavigate hook for navigation
import { useNavigate } from 'react-router-dom';

const ProfileWish = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 6;
  
  // Initialize the navigate function
  const navigate = useNavigate();
  
  // Get user from Redux store
  const user = useSelector(selectUser);
  const buyerId = user?.id;

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!buyerId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await axios.get(
          `https://propertydealstalk.com/api/buyer/getFavoritesByBuyerId?buyer_id=${buyerId}`
        );
        
        if (response.data.statusCode === 200) {
          setFavorites(response.data.result);
          // Calculate total pages
          setTotalPages(Math.ceil(response.data.result.length / itemsPerPage));
        } else {
          setError('Failed to fetch favorites');
        }
      } catch (err) {
        setError('Error fetching favorites: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [buyerId]);

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return favorites.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when changing pages (optional)
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Format price function
  const formatPrice = (price) => {
    if (!price) return '';
    
    // Convert to string and add commas for Indian numbering system
    return '₹' + price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Format base64 image function
  const formatImageUrl = (base64String) => {
    if (!base64String) return 'https://via.placeholder.com/300x150?text=No+Image';
    
    // Check if the string already has the data URL prefix
    if (base64String.startsWith('data:image')) {
      return base64String;
    }
    
    // If it's just the base64 string without prefix, add it
    return `data:image/jpeg;base64,${base64String}`;
  };

  // Function to handle navigation to property details
  const handlePropertyClick = (propertyId) => {
    // Navigate to the TalkView component with the property ID
    navigate(`/view/${propertyId}`);
  };

  if (loading) return <Loader />;
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: '25px',
        mb: '20px',
        ml: '20px',
        mr: '80px',
        width: '100%',
        maxWidth: '1000px',
        border: '2px solid #f1f1f1',
        p: '40px 20px',
        borderRadius: '16px',
        boxSizing: 'border-box',
        background: '#fff',
        '@media (max-width: 768px)': {
          m: '20px 10px',
        },
      }}
    >
      {favorites.length === 0 ? (
        <Typography sx={{ textAlign: 'center', p: 3 }}>
          No favorites found. Add properties to your wishlist!
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '40px',
              '@media (max-width: 760px)': {
                gridTemplateColumns: 'repeat(2, 240px)',
                justifyContent: 'space-between',
              },
              '@media (max-width: 500px)': {
                gridTemplateColumns: 'repeat(1, 240px)',
                justifyContent: 'center',
              },
            }}
          >
            {getCurrentPageItems().map(item => {
              const property = item.property;
              
              // Handle base64 image
              let imageUrl = 'https://via.placeholder.com/300x150?text=No+Image';
              if (property.primary_image) {
                imageUrl = formatImageUrl(property.primary_image);
              } else if (property.images && property.images.length > 0) {
                // Assuming the first image in the array is what you want to display
                imageUrl = formatImageUrl(property.images);
              }
                  
              return (
                <Box
                  key={item.favorite_id}
                  sx={{
                    background: '#fff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                  // Add onClick handler to navigate to property details
                  onClick={() => handlePropertyClick(property.id)}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={property.property_name}
                    sx={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      p: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      flexGrow: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        component="h4"
                        sx={{ m: 0, fontSize: '14px' }}
                      >
                        {property.property_name}
                      </Typography>
                      <Box
                        component="img"
                        src={heartIcon}
                        alt="heart icon"
                        sx={{ 
                          width: '18px', 
                          height: '18px',
                          filter: 'brightness(0) saturate(100%) invert(15%) sepia(95%) saturate(6932%) hue-rotate(359deg) brightness(95%) contrast(112%)' // Makes it red
                        }}
                        // Stop propagation to prevent navigation when clicking the heart icon
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Box>
                    <Typography sx={{ m: '4px 0 0', fontSize: '12px', color: '#555' }}>
                      {property.property_type === 'residential' ? 'Residential' : 'Commercial'} {property.asset_type}
                    </Typography>
                    <Typography sx={{ m: '4px 0 0', fontSize: '12px', color: '#555' }}>
                      {property.area_name}, {property.location}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#2563EB !important',
                        fontWeight: 600,
                        fontSize: '15px !important',
                        m: '4px 0 0',
                      }}
                    >
                      {formatPrice(property.asking_price)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
          
          {/* Pagination component - only show if we have more than one page */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#333',
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#2563EB !important',
                    color: 'white',
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ProfileWish;